import _, { debounce } from 'lodash';
import React, { useMemo, useState, useCallback } from 'react';

import { Box } from '@mui/system';
import {
  Chip,
  Badge,
  Stack,
  Button,
  Select,
  Popover,
  Tooltip,
  MenuItem,
  useTheme,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  useMediaQuery,
  InputAdornment
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { EntityLabelType } from 'src/services/utils/utils.types';
import { useSearchLabelsQuery } from 'src/services/utils/utils.service';
import { CandidateAvailabilityStatus } from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import { TableProps } from 'src/components/table';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';


export interface ToolbarOptions {
  addButton: boolean;
}

export type Props = {
  table?: TableProps;
  onFilters: (type: string, value: any) => void;
  clearFilters: () => void;
};

export default function PlacementsTableToolbar({
  table,
  onFilters,
  clearFilters,
}: Props) {

  const [searchValue, setSearchValue] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

  const actionsControl = usePopover();

  const { canAccess } = useFeatures();

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onClearFilters = () => {
    setSearchValue('');
    clearFilters();
  };

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('search_query', event.target.value);
      setSearchValue(event.target.value);
    },
    [onFilters]
  );

  const handleOpenFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  }

  const disableReset = useMemo(() => !searchValue, [searchValue]);
  const filterActiveCount = useMemo(() => {
    let count = 0;
    if (searchValue) count++;

    return count;
  }, [searchValue]);

  const filterContent = (
    <Stack spacing={2} p={1} display="flex" direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center', alignContent: 'center', width: '100%' }} flexWrap="wrap">
      <Stack spacing={2} display="flex" direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}>
        <TextField
          onChange={handleFilterName}
          placeholder={t('common.placeholders.search')}
          value={searchValue}
          name="search"
          sx={{ width: { sm: '300px', xs: '100%' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        {isMobile ? (
          <Button
            variant="text"
            disabled={disableReset}
            onClick={onClearFilters}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}
          >
            {t('common.clear')}

            {
              filterActiveCount > 0 && <Chip color="error" label={filterActiveCount} size='small' />
            }

          </Button>
        ) :
          <FormControl>
            <Tooltip title={t('common.clear')}>

              <IconButton
                size="large"
                disabled={disableReset}
                onClick={onClearFilters}
                aria-label={t('common.clear')}
              >
                <Badge invisible={!filterActiveCount} color="error" badgeContent={filterActiveCount}>
                  <Iconify icon="material-symbols:cancel" />
                </Badge>
              </IconButton>
            </Tooltip>
          </FormControl>
        }
      </Stack>
    </Stack >
  );

  return (
    <Stack
      spacing={2}
      display="flex"
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent={{ xs: 'center', sm: 'space-between' }}
      sx={{
        p: 2,
        width: '100%',
      }}
    >
      {isMobile ? (
        <>
          <Badge invisible={!filterActiveCount} color="error" badgeContent={filterActiveCount} sx={{ marginRight: 1 }} >
            <Button
              variant="outlined"
              onClick={handleOpenFilter}
              fullWidth
              startIcon={<Iconify icon="mdi:filter" />}
            >

              {t('common.filter')}
            </Button>
          </Badge>

          <Popover
            sx={{ padding: 2 }}
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleCloseFilter}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {filterContent}
          </Popover>
        </>
      ) : (
        <Stack direction="column" sx={{ width: '100%' }} spacing={1}>
          <Stack sx={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', 'width': '100%' }} gap={2}>

            {filterContent}

            <Box display="flex" alignContent="center" justifyContent="center" gap={2}>
              {/* 
              <Button
                size="medium"
                onClick={actionsControl.onOpen}
                startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
                variant="outlined"
                disabled={!canAccess(FeatureType.ADD_CANDIDATE_LIMIT)}
                sx={{ textWrap: 'nowrap' }}
              >
                {t('common.actions')}
              </Button> */}
            </Box>
          </Stack>

        </Stack>
      )}
    </Stack>
  );
}